<template>
	<contentBox v-if="!loadError" v-bind:loading="loading">
		<contentTitle v-if="zoning" label="EDIT ZONING" v-bind:close="routeClose">
			<h2 class="text-white font-weight-light pb-2">{{zoning.designation}}</h2>
		</contentTitle>
		<contentBody v-if="zoning" v-bind:locked="token === null || !isAdmin">
			<div class="p-3">
				<div class="lock-block" v-if="token === null || !isAdmin">
					<div class="row mx-0 hover-dk" v-on:click="routeLogin">
						<div class="col-2 text-center my-auto">
							<i class="material-icons text-light mr-3 md-36">account_box</i>
						</div>
						<div class="py-3 col-10 pl-0 pr-3 py-3">
							<h5 class="mb-0 text-white">Sign in to edit</h5>
							<h5 class="mb-0 font-weight-light text-light">You must be signed in as an admin to edit this record</h5>
						</div>
					</div>
				</div>
				<div v-if="token !== null && isAdmin" class="category-body">
					<div v-if="mode === 'detailsEdit'" class="section section-bold">
						<div class="section-title">
							Edit Details
							<span v-on:click="setMode(null)" class="category-title-btn"><i class="material-icons md-14">close</i></span>
						</div>
						<div class="section-body">
							<div class="category-title">JURISDICTION</div>
							<div class="property-row">
								<div class="text-description">{{zoning.jurisdiction.name}}</div>
							</div>
							<div class="category">
								<div class="category-title">DESCRIPTION</div>
								<div class="property-row">
									<textarea class="form-control" v-model="rec.description"></textarea>
								</div>
							</div>
							<div class="category">
								<div class="category-title">USE</div>
								<div class="property-row">
									<label>Primary</label>
									<div class="property-value-right">
										<select v-model.string="rec.use.primary" v-on:change="primaryUseChange" class="form-control form-control-sm" placeholder="primary use">
											<option value="">
												** blank **
											</option>
											<option v-for="(use) in zoningUses.primary" :value="use">
												{{use}}
											</option>
										</select>
									</div>
								</div>
								<div class="property-row">
									<label>Secondary</label>
									<div class="property-value-right">
										<span v-if="!rec.use.primary">
											Select a primary use
										</span>
										<span v-else-if="!zoningUses.secondary[rec.use.primary]">
											No secondary uses for {{rec.use.primary}}
										</span>
										<select v-else v-model.string="rec.use.secondary" class="form-control form-control-sm" placeholder="secondary use">
											<option value="">
												** blank **
											</option>
											<option v-for="(use) in zoningUses.secondary[rec.use.primary]" :value="use">
												{{use}}
											</option>
										</select>
									</div>
								</div>
							</div>
						</div>
						<div class="section-footer">
							<button v-on:click="saveDetails" class="btn btn-sm btn-primary float-right">Save</button>
						</div>
					</div>
					<div v-else class="section">
						<div class="section-title">
							Details
							<span v-on:click="setMode('detailsEdit')" class="category-title-btn"><i class="material-icons md-14">edit</i></span>
						</div>
						<div class="section-body">
							<div class="category-title">JURISDICTION</div>
							<div class="property-row">
								<div class="text-description">{{zoning.jurisdiction.name}}</div>
							</div>
							<div class="category">
								<div class="category-title">DESCRIPTION</div>
								<div v-if="zoning.description" class="property-row">
									{{zoning.description}}
								</div>
								<span v-else class="undef">
									-
								</span>
							</div>
							<div class="category">
								<div class="category-title">USE</div>
								<div class="property-row">
									<label>Primary</label>
									<span v-if="zoning.use.primary" class="property-value-right">
										{{zoning.use.primary}}
									</span>
									<span v-else class="undef property-value-right">
										-
									</span>
								</div>
								<div class="property-row">
									<label>Secondary</label>
									<span v-if="zoning.use.secondary" class="property-value-right">
										{{zoning.use.secondary}}
									</span>
									<span v-else class="undef property-value-right">
										-
									</span>
								</div>
							</div>
						</div>
					</div>

					<div v-for="(regulation, regInd) in zoning.regulations">
						<regulationForm 
							v-if="mode === 'regulationEdit' && regulationEditInd === regInd" 
							@close="setMode(null)" 
							@submit="regulationSave"
							:regulation="zoning.regulations[regInd]" 
							submitLabel="Save" 
							:title="`Edit Regulation ${regInd+1}`"/>
						<div v-else-if="mode === 'regulationDelete' && regulationDeleteInd === regInd" class="section section-bold">
							<div class="section-title">
								Delete Regulation {{regInd+1}}
								<span v-on:click="setMode(null)" class="category-title-btn"><i class="material-icons md-14">close</i></span>
							</div>
							<div class="section-body">
								<div class="property-row">
									Are you sure you want to delete this regulation?
								</div>
							</div>
							<div class="section-footer">
								<button v-on:click="regulationDeleteSubmit" class="btn btn-sm btn-danger float-right">Delete</button>
							</div>
						</div>
						<div v-else class="section">
							<div class="section-title">
								Regulation {{regInd+1}}
								<span v-on:click="regulationDelete(regInd)" class="category-title-btn"><i class="material-icons md-14">delete</i></span>
								<span v-on:click="regulationEdit(regInd)" class="category-title-btn"><i class="material-icons md-14">edit</i></span>
							</div>
							<div class="section-body">
								<div v-if="regulation.lotSize.minArea !== null || regulation.lotSize.maxArea !== null ||
									regulation.lotSize.minWidth !== null || regulation.lotSize.minWidthCorner !== null || 
									regulation.lotSize.minFrontage !== null || regulation.lotSize.minDepth !== null" class="category">
									<div class="category-title">LOT SIZE</div>
									<div class="property-row" v-if="regulation.lotSize.minArea !== null">
										<label>Minimum area</label>

										<div class="property-value-right">
											{{regulation.lotSize.minArea | integerLg}} ft²
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotSize.maxArea !== null">
										<label>Maximum area</label>
										<div class="property-value-right">
											{{regulation.lotSize.maxArea | integerLg}} ft²
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotSize.minWidth !== null">
										<label>Minimum width</label>
										<div class="property-value-right">
											{{regulation.lotSize.minWidth | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotSize.minWidthCorner !== null">
										<label>Minimum width corner</label>
										<div class="property-value-right">
											{{regulation.lotSize.minWidthCorner | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotSize.minFrontage !== null">
										<label>Minimum frontage</label>
										<div class="property-value-right">
											{{regulation.lotSize.minFrontage | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotSize.minDepth !== null">
										<label>Minimum depth</label>
										<div class="property-value-right">
											{{regulation.lotSize.minDepth | integerLg}} ft
										</div>
									</div>
								</div>
								<div v-if="regulation.density.sfDu !== null || regulation.density.sfDuNote !== null" class="category">
									<div class="category-title">DENSITY</div>
									<div class="property-row">
										<label>SF/DU</label>
										<div class="property-value-right">
											{{regulation.density.sfDu | integerLg}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.density.sfDuNote !== null"> 
										<label>SF/DU note</label>
										<div class="property-value-right">
											{{regulation.density.sfDuNote}}
										</div>
									</div>
								</div>
								<div v-if="regulation.heightLimit.max !== null || regulation.heightLimit.aboveEnclosedParking !== null || regulation.heightLimit.roofFlat !== null || regulation.heightLimit.roofPitched !== null" class="category">
									<div class="category-title">HEIGHT LIMIT</div>
									<div class="property-row" v-if="regulation.heightLimit.max !== null">
										<label>Height maximum</label>
										<div class="property-value-right">
											{{regulation.heightLimit.max | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.heightLimit.aboveEnclosedParking !== null">
										<label>Above enclosed parking</label>
										<div class="property-value-right">
											{{regulation.heightLimit.aboveEnclosedParking | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.heightLimit.roofFlat !== null">
										<label>Roof flat limit</label>
										<div class="property-value-right">
											{{regulation.heightLimit.roofFlat | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.heightLimit.roofPitched !== null">
										<label>Roof pitched limit</label>
										<div class="property-value-right">
											{{regulation.heightLimit.roofPitched | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.heightLimit.note !== null">
										<label>Height note</label>
										<div class="property-value-right">
											{{regulation.heightLimit.note}} ft
										</div>
									</div>
								</div>
								<div v-if="regulation.far.base !== null || regulation.far.min !== null || 
									regulation.far.max !== null || regulation.far.residential !== null || 
									regulation.far.commercial !== null || regulation.far.mixed !== null" class="category">
									<div class="category-title">FLOOR TO AREA RATIO (FAR)</div>
									<div class="property-row" v-if="regulation.far.base !== null">
										<label>FAR base</label>
										<div class="property-value-right">
											{{regulation.far.base}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.far.min !== null">
										<label>FAR minimum</label>
										<div class="property-value-right">
											{{regulation.far.min}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.far.max !== null">
										<label>FAR maximum</label>
										<div class="property-value-right">
											{{regulation.far.max}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.far.residential !== null">
										<label>FAR residential</label>
										<div class="property-value-right">
											{{regulation.far.residential}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.far.commercial !== null">
										<label>FAR commercial</label>
										<div class="property-value-right">
											{{regulation.far.commercial}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.far.mixed !== null">
										<label>FAR mixed</label>
										<div class="property-value-right">
											{{regulation.far.mixed}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.far.note !== null">
										<label>FAR note</label>
										<div class="property-value-right">
											{{regulation.far.note}}
										</div>
									</div>
								</div>
								<div v-if="regulation.setbacks.frontMin !== null || regulation.setbacks.frontMax !== null || 
									regulation.setbacks.interiorSide !== null || regulation.setbacks.streetSide !== null || 
									regulation.setbacks.rear !== null || regulation.setbacks.rearAlley !== null ||
									regulation.setbacks.rearAlley !== null || regulation.setbacks.generalNote !== null" class="category">
									<div class="category-title">SETBACKS</div>
									<div class="property-row" v-if="regulation.setbacks.frontMin !== null">
										<label>Front minimum</label>
										<div class="property-value-right">
											{{regulation.setbacks.frontMin | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.frontMinNote !== null">
										<label>Front minimum note</label>
										<div class="property-value-right">
											{{regulation.setbacks.frontMinNote}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.frontMax !== null">
										<label>Front maximum</label>
										<div class="property-value-right">
											{{regulation.setbacks.frontMax | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.frontMaxNote !== null">
										<label>Front maximum note</label>
										<div class="property-value-right">
											{{regulation.setbacks.frontMaxNote}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.interiorSide !== null">
										<label>Interior side</label>
										<div class="property-value-right">
											{{regulation.setbacks.interiorSide | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.interiorSideNote !== null">
										<label>Interior side note</label>
										<div class="property-value-right">
											{{regulation.setbacks.interiorSideNote}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.streetSide !== null">
										<label>Street side</label>
										<div class="property-value-right">
											{{regulation.setbacks.streetSide | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.streetSideNote !== null">
										<label>Street side note</label>
										<div class="property-value-right">
											{{regulation.setbacks.streetSideNote}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.rear !== null">
										<label>Rear</label>
										<div class="property-value-right">
											{{regulation.setbacks.rear | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.rearAlley !== null">
										<label>Rear Alley</label>
										<div class="property-value-right">
											{{regulation.setbacks.rearAlley | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.rearNote !== null">
										<label>Rear note</label>
										<div class="property-value-right">
											{{regulation.setbacks.rearNote}}
										</div>
									</div>
									<div class="property-row" v-if="regulation.setbacks.generalNote !== null">
										<label>General note</label>
										<div class="property-value-right">
											{{regulation.setbacks.generalNote}}
										</div>
									</div>
								</div>
								<div v-if="regulation.lotCoverage.min !== null || regulation.lotCoverage.max !== null" class="category">
									<div class="category-title">LOT COVERAGE</div>
									<div class="property-row" v-if="regulation.lotCoverage.min !== null">
										<label>Lot coverage minimum</label>
										<div class="property-value-right">
											{{regulation.lotCoverage.min | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotCoverage.max !== null">
										<label>Lot coverage maximum</label>
										<div class="property-value-right">
											{{regulation.lotCoverage.max | integerLg}} ft
										</div>
									</div>
									<div class="property-row" v-if="regulation.lotCoverage.note !== null">
										<label>Lot coverage note</label>
										<div class="property-value-right">
											{{regulation.lotCoverage.note}}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<regulationForm 
						v-if="mode === 'regulationAdd'" 
						@close="setMode(null)"
						@submit="regulationAdd"
						submitLabel="Add" 
						title="Add Regulation"/>
					<div v-else class="mb-3">
						<button v-on:click="setMode('regulationAdd')" class="btn btn-dark btn-sm">Add Regulation</button>
					</div>

					<div v-if="zoning.references" v-for="(ref, idx) in zoning.references">
						<div v-if="mode === 'referenceDelete' && referenceDeleteInd === idx" class="section section-bold">
							<div class="section-title">
								Delete Reference {{idx+1}}
								<span v-on:click="setMode(null)" class="category-title-btn"><i class="material-icons md-14">close</i></span>
							</div>
							<div class="section-body">
								<div class="property-row">
									Are you sure you want to delete this reference?
								</div>
							</div>
							<div class="section-footer">
								<button v-on:click="referenceDeleteSubmit" class="btn btn-sm btn-danger float-right">Delete</button>
							</div>
						</div>
						<referenceForm 
							v-else-if="mode === 'referenceEdit' && referenceEditInd === idx" 
							@close="setMode(null)" 
							@submit="referenceSave"
							:reference="zoning.references[idx]" 
							submitLabel="Save" 
							:title="`Edit Reference ${idx+1}`"/>
						<div v-else class="section">
							<div class="section-title">
								Reference {{idx+1}}
								<span v-on:click="referenceDelete(idx)" class="category-title-btn"><i class="material-icons md-14">delete</i></span>
								<span v-on:click="referenceEdit(idx)" class="category-title-btn"><i class="material-icons md-14">edit</i></span>
							</div>
							<div class="section-body">
								<div class="category">
									<div class="category-title">DESCRIPTION</div>
									<div v-if="ref.description" class="property-row">
										{{ref.description}}
									</div>
									<span v-else class="undef">
										-
									</span>
								</div>
								<div class="category">
									<div class="category-title">URL</div>
									<div v-if="ref.url" class="property-row word-wrap-break">
										{{ref.url}}
									</div>
									<span v-else class="undef">
										-
									</span>
								</div>
							</div>
						</div>
					</div>
					<referenceForm 
						v-if="mode === 'referenceAdd'" 
						@close="setMode(null)" 
						@submit="referenceAdd"
						submitLabel="Add" 
						title="Add Reference"/>
					<div v-else class="mb-3">
						<button v-on:click="setMode('referenceAdd')" class="btn btn-dark btn-sm">Add Reference</button>
					</div>

				</div>
			</div>
			
		</contentBody>
	</contentBox>
</template>

<script>

import contentBox from '../content';
import contentActions from '../content/content-actions';
import contentBody from '../content/content-body';
import contentTitle from '../content/content-title';
import referenceForm from './reference-form';
import regulationForm from './regulation-form';

import zoningUses from '../../config/zoning-uses';

export default {
	name: 'zoning',

	components:{
		contentBox,
		contentActions,
		contentBody,
		contentTitle,
		referenceForm,
		regulationForm,
	},

	computed: {
		isAdmin: function(){
			return this.$store.state.agent.rec && this.$store.state.agent.rec.role.name && this.$store.state.agent.rec.role.name === 'admin';
		},
		token: function(){
			return this.$store.state.agent.token;
		},
		zoning: function() {
			return this.$store.state.zoning.rec;
		},
		mapCenter: function(){
			return {lat:this.$store.state.map.params.lat,lng:this.$store.state.map.params.lng};
		},
		mapZoom: function(){
			return this.$store.state.map.params.zoom;
		},
		mapLoaded:function(){
			return this.$store.state.map.loaded;
		},
		mapBounds:function(){
			return this.$store.state.map.bounds;
		},
		flyToDone:function(){
			return this.$store.state.map.flyToDone;
		},
		zoningUses:function(){
			return zoningUses
		}
	},

	created: function(){
		this.recLoaded = false;
		this.getRec();
	},
	data: function(){
		return {
			error:null,
			mode:null, // detailsEdit, regulationEdit, regulationAdd, 
			rec:null,
			referenceDeleteInd:null,
			referenceEditInd:null,
			regulationDeleteInd:null,
			regulationEditInd:null,
			loading:true,
			loadError:null,
		}
	},

	destroyed: function(){
		this.$store.dispatch('ZONING_HIGHLIGHT_CLEAR',{});
	},

	methods: {
		getRec: function(){
			this.loadError = null;
			this.loading = true;
			this.$store.dispatch('ZONING_GET', {
				id: this.$route.params.id
			}).catch((error)=>{
				this.loadError = error;
			});
		},
		routeClose:function(){
			this.$router.push({
				name: 'zoning', 
				params:{id:this.$route.params.id}
			});
		},
		routeLogin:function(){
			this.$router.push({
				name: 'auth', 
				params:{}
			});
		},
		highlight:function(){
			this.$store.dispatch('ZONING_HIGHLIGHT',{});
		},
		zoomTo:function(){
			this.$store.dispatch('ZONING_ZOOM_TO'/*,this.mapCenter*/);
		},
		checkInside:function(){
			const bbox = this.zoning.bounds.bbox;
			const bounds = this.mapBounds;
			if (bbox[1] > bounds._ne.lat || bbox[3] < bounds._sw.lat || 
				bbox[0] > bounds._ne.lng || bbox[2] < bounds._sw.lng){
				return false;
			}
			return true;
		},
		checkZoom:function(){
			if (this.mapZoom >= 16 || this.mapZoom <= 12) return false;
			return true;
		},
		checkReady:function(){ //called to check if and resolve when all resources have been loaded
			if (!this.zoning || !this.mapLoaded) return;
			this.ready();
		},
		primaryUseChange:function(){ // blank out secondary use on primary use change
			this.rec.use.secondary = ''
		},
		ready:function(){
			if (!this.checkInside()) this.zoomTo();
			else if (!this.checkZoom()){
				this.$store.dispatch('MAP_FLY_TO',{
					zoom:16
				});
			}
			else this.highlight();
			this.updateTitleTag();
		},
		referenceAdd:async function(reference){
			this.loading = true;
			await this.$store.dispatch('ZONING_REFERENCE_ADD', {
				id:this.$route.params.id, 
				reference:reference
			});
			this.loading = false;
			this.setMode(null);
		},
		referenceDelete:function(ind){
			this.setMode('referenceDelete');
			this.referenceDeleteInd = ind;
		},
		referenceDeleteSubmit:async function(){
			this.loading = true;
			const referenceId = this.zoning.references[this.referenceDeleteInd].id;
			await this.$store.dispatch('ZONING_REFERENCE_DELETE', {
				id:this.$route.params.id, 
				referenceId:referenceId
			});
			this.loading = false;
			this.setMode(null);
		},
		referenceEdit:function(ind){
			this.setMode('referenceEdit');
			this.referenceEditInd = ind;
		},
		referenceSave:async function(reference){
			this.loading = true;
			const referenceId = this.zoning.references[this.referenceEditInd].id;
			await this.$store.dispatch('ZONING_REFERENCE_SAVE', {
				id:this.$route.params.id, 
				referenceId:referenceId,
				reference:reference
			});
			this.loading = false;
			this.setMode(null);
		},
		regulationAdd:async function(regulation){
			this.loading = true;
			await this.$store.dispatch('ZONING_REGULATION_ADD', {
				id:this.$route.params.id, 
				regulation:regulation
			});
			this.loading = false;
			this.setMode(null);
		},
		regulationDelete:function(ind){
			this.setMode('regulationDelete');
			this.regulationDeleteInd = ind;
		},
		regulationDeleteSubmit:async function(){
			this.loading = true;
			const regulationId = this.zoning.regulations[this.regulationDeleteInd].id;
			await this.$store.dispatch('ZONING_REGULATION_DELETE', {
				id:this.$route.params.id, 
				regulationId:regulationId
			});
			this.loading = false;
			this.setMode(null);
		},
		regulationEdit:function(ind){
			this.setMode('regulationEdit');
			this.regulationEditInd = ind;
		},
		regulationSave:async function(regulation){
			this.loading = true;
			const regulationId = this.zoning.regulations[this.regulationEditInd].id;
			await this.$store.dispatch('ZONING_REGULATION_SAVE', {
				id:this.$route.params.id, 
				regulationId:regulationId,
				regulation:regulation
			});
			this.loading = false;
			this.setMode(null);
		},
		updateTitleTag:function(){
			if (this.zoning && this.zoning.designation) return document.title = 'Zoning '+this.zoning.designation+' - Scoutred Maps';
			return document.title = 'Zoning - Scoutred Maps';
		},
		saveDetails:async function(){
			this.loading = true;
			await this.$store.dispatch('ZONING_SAVE', {
				id:this.$route.params.id, 
				rec:this.rec
			});
			this.loading = false;
			this.setMode(null);
		},
		setMode:function(mode){
			this.mode = mode;
		},
	},

	watch: {
		$route:function(toRoute, fromRoute){
			if (fromRoute && toRoute.params.id !== fromRoute.params.id){ // route resource has changed
				this.getRec();
			}
		},
		mapLoaded:function(){
			this.checkReady();
		},
		zoning:function(toRec, fromRec){
			this.checkReady();
			if (this.zoning){
				this.rec = JSON.parse(JSON.stringify(this.zoning))
				this.loading = false;
			}
		},
		flyToDone:function(){
			this.highlight();
		}
	},
}

</script>



// WEBPACK FOOTER //
// src/components/zoning/zoning-edit.vue